///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One story annotation on the globe: an arc (geodesic polyline, optional
/// animated flow arrows), a marker (EmberDome in the placemark style), or
/// a region highlight (filled polygon + outline, like alert areas). Fades
/// in/out as stops change so annotations belonging to other stops don't
/// pop.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { PolygonGeometry } from "@/earthbrowser/entities/geometries/PolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { cDefaultStoryAnnotationColor, cStoryAnnotationColors, type StoryAnnotation } from "@/redux/storiesSlice"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { rgba, type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { vec2 } from "@stovokor/math/vector"
import { slerpUnit, surfacePoint, unitNormal } from "../hurricanes/stormTrack"
import { type DatasetSceneObject } from "../DatasetLayer"
import { type StoryAnnotationFeature } from "./StoryLayer"

// Draped just above the surface; arcs ride higher so long hops read as
// routes rather than terrain-hugging borders
const cRegionAltitudeKm = 0.5
const cArcAltitudeKm = 3
const cOutlineLiftKm = 0.15

const cArcWidth = 2.5
const cOutlineWidth = 1.5
/** Flow arrows per arc when directional */
const cArrowCount = 12

const cArcAlpha = 0.9
const cFillAlpha = 0.16
const cOutlineAlpha = 0.85

// Marker dome: same visual family as placemark pins, slightly smaller so
// story markers read as annotation, not user data
const cMarkerRadiusKm = 0.5
const cMarkerMinAngular = 0.002
const cMarkerMaxAngular = 0.006
const cMarkerFill = 0.85
const cMarkerAlpha = 0.9
const cMarkerHeightKm = 0.02

/** Seconds for the stop-change fade in/out */
const cFadeSeconds = 0.35

/** Max geodesic step between polyline vertices (radians, ~0.35°) */
const cMaxStepRad = 0.006

/** Parse a story palette key ("amber") into an engine color */
export function storyPaletteColor(key: string, alpha: number): color {
	const hex = cStoryAnnotationColors[key] ?? cStoryAnnotationColors[cDefaultStoryAnnotationColor]
	const r = parseInt(hex.slice(1, 3), 16) / 255
	const g = parseInt(hex.slice(3, 5), 16) / 255
	const b = parseInt(hex.slice(5, 7), 16) / 255
	return rgba(r, g, b, alpha)
}

/** Geodesically densified world positions for a lat/lon path */
export function densifiedPathPositions(
	points: { lat: number; lon: number }[],
	altitudeKm: number,
	loop = false
) {
	const path = loop && points.length > 2 ? [...points, points[0]] : points
	const positions = []
	let prev = unitNormal(path[0].lat, path[0].lon)
	positions.push(surfacePoint(prev, altitudeKm))
	for (let i = 1; i < path.length; i++) {
		const next = unitNormal(path[i].lat, path[i].lon)
		const angle = Math.acos(Math.max(-1, Math.min(1,
			prev.x * next.x + prev.y * next.y + prev.z * next.z)))
		const steps = Math.max(1, Math.ceil(angle / cMaxStepRad))
		for (let s = 1; s <= steps; s++) {
			positions.push(surfacePoint(slerpUnit(prev, next, s / steps), altitudeKm))
		}
		prev = next
	}
	return positions
}

export class StoryAnnotationNode extends NodeBase implements DatasetSceneObject<StoryAnnotationFeature> {
	public feature: StoryAnnotationFeature

	private context: EarthBrowserContext
	private dome?: EmberDomeNode
	private domeScale = cMarkerRadiusKm
	private lines: { node: PolylineNode; color: color }[] = []
	private fills: { node: SimpleNode; color: color }[] = []

	/** Fade state: 0 hidden, 1 shown; eased toward the feature's shown flag */
	private fade: number
	private lastRenderTime?: number

	constructor(context: EarthBrowserContext, feature: StoryAnnotationFeature) {
		super()
		this.context = context
		this.feature = feature
		// Annotations of the launching stop appear without a fade-in pop
		this.fade = feature.shown ? 1 : 0
		this.build(feature.annotation)
	}

	public updateFeature(feature: StoryAnnotationFeature): void {
		const geometryChanged = feature.annotation !== this.feature.annotation
		this.feature = feature
		if (geometryChanged) {
			this.teardown()
			this.build(feature.annotation)
		}
	}

	private teardown(): void {
		for (const { node } of this.lines) {
			this.removeNode(node)
			node.destroy()
		}
		for (const { node } of this.fills) {
			this.removeNode(node)
			node.destroy()
		}
		if (this.dome) {
			this.removeNode(this.dome)
			this.dome.destroy()
			this.dome = undefined
		}
		this.lines = []
		this.fills = []
		this.transform = matrix.identity
	}

	private build(annotation: StoryAnnotation): void {
		if (annotation.kind === "marker") {
			this.buildMarker(annotation.lat, annotation.lon, annotation.color)
		} else if (annotation.kind === "arc") {
			this.buildArc(annotation.points, annotation.color, annotation.directional === true)
		} else {
			this.buildRegion(annotation.ring, annotation.color)
		}
		this.applyFade()
	}

	private buildMarker(lat: number, lon: number, colorKey: string): void {
		const coordinate = new geographic(lon * trig.rad, lat * trig.rad, 0)
		const altitudeKm = this.context.displayElevationKm(coordinate) + cMarkerHeightKm
		const placed = new geographic(lon * trig.rad, lat * trig.rad, altitudeKm)

		this.dome = new EmberDomeNode(this.context, storyPaletteColor(colorKey, cMarkerAlpha))
		this.dome.fillAlpha = cMarkerFill
		this.dome.minAngularSize = cMarkerMinAngular
		this.dome.transform = matrix.scale(cMarkerRadiusKm)
		this.transform = matrix.translate(placed.cartesian)
		this.addNode(this.dome)
	}

	private buildArc(points: { lat: number; lon: number }[], colorKey: string, directional: boolean): void {
		if (points.length < 2) return
		const lineColor = storyPaletteColor(colorKey, cArcAlpha)
		const line = new PolylineNode(this.context, {
			positions: densifiedPathPositions(points, cArcAltitudeKm),
			width: cArcWidth,
			color: lineColor,
			depthWrite: false,
			arrowCount: directional ? cArrowCount : 0,
		})
		this.lines.push({ node: line, color: lineColor })
		this.addNode(line)
	}

	private buildRegion(ring: { lat: number; lon: number }[], colorKey: string): void {
		if (ring.length < 3) return

		// Filled interior; poly2tri can throw on self-intersecting rings,
		// in which case the outline still communicates the region
		const fillColor = storyPaletteColor(colorKey, cFillAlpha)
		try {
			const ringVec2 = ring.map(p => new vec2(p.lon, p.lat))
			const bounds = {
				minLon: Math.min(...ring.map(p => p.lon)),
				minLat: Math.min(...ring.map(p => p.lat)),
				maxLon: Math.max(...ring.map(p => p.lon)),
				maxLat: Math.max(...ring.map(p => p.lat)),
			}
			const geometry = new PolygonGeometry(this.context, [ringVec2], bounds, cRegionAltitudeKm)
			const fill = new SimpleNode(this.context, geometry, fillColor)
			this.fills.push({ node: fill, color: fillColor })
			this.addNode(fill)
		} catch {
			// outline-only fallback
		}

		const outlineColor = storyPaletteColor(colorKey, cOutlineAlpha)
		const outline = new PolylineNode(this.context, {
			positions: densifiedPathPositions(ring, cRegionAltitudeKm + cOutlineLiftKm, true),
			width: cOutlineWidth,
			color: outlineColor,
			depthWrite: false,
		})
		this.lines.push({ node: outline, color: outlineColor })
		this.addNode(outline)
	}

	/** Apply the current fade to every child's color/opacity */
	private applyFade(): void {
		if (this.dome) {
			this.dome.opacity = this.fade
		}
		for (const { node, color: base } of this.lines) {
			node.updateColor(rgba(base.r, base.g, base.b, base.a * this.fade))
		}
		for (const { node, color: base } of this.fills) {
			node.color = rgba(base.r, base.g, base.b, base.a * this.fade)
		}
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) return []

		// Ease the fade toward the shown flag (wall-clock dt; render runs
		// once per frame)
		const now = performance.now()
		const dt = this.lastRenderTime !== undefined ? Math.min(0.1, (now - this.lastRenderTime) / 1000) : 0
		this.lastRenderTime = now

		const target = this.feature.shown ? 1 : 0
		if (this.fade !== target) {
			const step = dt / cFadeSeconds
			this.fade = target > this.fade
				? Math.min(target, this.fade + step)
				: Math.max(target, this.fade - step)
			this.applyFade()
		}

		if (this.fade <= 0.001) return []

		// Marker apparent-size ceiling so it stays a discrete dot up close
		if (this.dome && this.feature.annotation.kind === "marker") {
			const distance = state.camera.position.subtract(this.transform.position).length
			const targetScale = Math.max(
				Math.min(cMarkerRadiusKm, distance * cMarkerMaxAngular),
				0.001
			)
			if (Math.abs(targetScale - this.domeScale) > this.domeScale * 0.02) {
				this.domeScale = targetScale
				this.dome.transform = matrix.scale(targetScale)
			}
		}

		return super.render(state)
	}

	override destroy(): void {
		this.teardown()
		super.destroy()
	}
}
